import React, { memo, useMemo, useCallback, useState, useEffect } from "react";
import { Sparkles, ArrowRight, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardFooter, 
  CardHeader, 
  CardTitle 
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { useNavigate } from "react-router-dom";
import { cn } from "@/lib/utils";
import { formatDistanceToNow } from "date-fns";
import { useSession } from "@/hooks/use-session";
import { useQuery } from "@tanstack/react-query";
import { supabase } from '@/integrations/supabase/client';

// Import types from the API utils
import type { MarketAlert, CalendarAlert, NewsAlert } from '@/utils/api';
import { useAlerts } from '@/hooks/use-alerts';
import MarketAlertCard from './MarketAlertCard';
import CalendarAlertCard from './CalendarAlertCard';

// Utility functions for badge colors
const volatilityColorMap = {
  high: "bg-red-900/40 text-red-400 border-red-900/50",
  medium: "bg-yellow-900/40 text-yellow-400 border-yellow-900/50",
  low: "bg-green-900/40 text-green-400 border-green-900/50",
  critical: "bg-red-900/40 text-red-400 border-red-900/50"
};

const getVolatilityColor = (intensity: string = 'medium') => {
  return volatilityColorMap[intensity?.toLowerCase() as keyof typeof volatilityColorMap] || 
         "bg-blue-900/40 text-blue-400 border-blue-900/50";
};

// Custom hooks for data fetching to encapsulate API logic
const useMarketAlerts = (userId?: string) => {
  return useQuery<MarketAlert[]>({
    queryKey: ['risk-alerts', userId],
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke('risk-alerts', {
        body: { limit: 5 }
      });
      
      if (error) throw error;
      return (data?.alerts || []);
    },
    enabled: !!userId,
    staleTime: 1000 * 60 * 5, // 5 minutes 
    gcTime: 1000 * 60 * 10,   // 10 minutes
    retry: 3
  });
};

const useCalendarAlerts = (userId?: string) => {
  return useQuery<CalendarAlert[]>({
    queryKey: ['calendar-alerts', userId],
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke('calendar-alerts', {
        body: { limit: 5 }
      });
      
      if (error) throw error;
      return (data?.alerts || []);
    },
    enabled: !!userId,
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 10,
    retry: 3
  });
};

// Compact alert item component for dashboard display
const CompactAlertItem = memo(({ alert, onNavigate }: { 
  alert: MarketAlert | CalendarAlert, 
  onNavigate: (id: number, type?: string) => void 
}) => {
  // Extract alert title and determine if it's a market or calendar alert
  const isMarketAlert = 'risk_level' in alert;
  // Extract the first sentence or segment for title
  const title = alert.short_summary.split(/[.\n]/)[0];
  
  // Determine badge styling based on alert type
  const getBadgeStyle = () => {
    if (isMarketAlert) {
      switch (alert.risk_level.toUpperCase()) {
        case 'HIGH': return 'bg-red-500/20 text-red-400 border-red-500/30';
        case 'MEDIUM': return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
        case 'LOW': return 'bg-green-500/20 text-green-400 border-green-500/30';
        default: return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
      }
    } else {
      const intensity = (alert.window_volatility_intensity || '').toLowerCase();
      switch (intensity) {
        case 'critical': 
        case 'high': return 'bg-red-500/20 text-red-400 border-red-500/30';
        case 'medium': return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
        case 'low': return 'bg-green-500/20 text-green-400 border-green-500/30';
        default: return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
      }
    }
  };
  
  // Function to safely render HTML content
  const createMarkup = (content: string) => {
    return { __html: content };
  };
  
  const badgeStyle = getBadgeStyle();
  const levelText = isMarketAlert 
    ? `${alert.risk_level} Risk` 
    : `${alert.window_volatility_intensity} Impact`;
    
  return (
    <div 
      className="bg-black/20 border border-white/10 hover:border-white/20 rounded-lg p-3 mb-2 cursor-pointer transition-all"
      onClick={() => onNavigate(alert.id, isMarketAlert ? 'market' : 'calendar')}
    >
      <div className="flex justify-between items-start gap-2">
        <h3 className="text-sm font-medium text-white line-clamp-1">{title}</h3>
        <Badge className={`shrink-0 ${badgeStyle} text-[10px] px-1.5 py-0`}>
          {levelText}
        </Badge>
      </div>
      <div className="text-xs text-white/70 line-clamp-1 mt-1" dangerouslySetInnerHTML={createMarkup(alert.short_summary)} />
      <div className="flex justify-end items-center mt-2">
        <div className="text-white/50 text-[10px] flex items-center">
          <Clock className="h-3 w-3 mr-1.5" />
          {formatDistanceToNow(new Date(alert.timestamp), { addSuffix: true })}
        </div>
      </div>
    </div>);
});

/**
 * Main component that combines the data and renders the UI
 */
export const StrykrAlertsCard = memo(({ className }: { className?: string }) => {
  const navigate = useNavigate();
  const {
    calendarAlerts = [],
    marketAlerts = [],
    newsAlerts = [],
    isLoadingCalendar = false,
    isLoadingMarket = false,
    isLoadingNews = false,
    errorCalendar = null,
    errorMarket = null,
    errorNews = null
  } = useAlerts();

  // State for expanded alerts (for preview functionality)
  const [expandedMarketId, setExpandedMarketId] = useState<number | null>(null);
  const [expandedCalendarId, setExpandedCalendarId] = useState<number | null>(null);
  const [expandedNewsId, setExpandedNewsId] = useState<number | null>(null);

  // Sort and slice alerts by type - show only the 2 most recent of each type
  const recentMarketAlerts = useMemo(() =>
    [...marketAlerts].sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()).slice(0, 2),
    [marketAlerts]
  );
  const recentCalendarAlerts = useMemo(() =>
    [...calendarAlerts].sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()).slice(0, 2),
    [calendarAlerts]
  );
  const recentNewsAlerts = useMemo(() =>
    [...newsAlerts].sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()).slice(0, 2),
    [newsAlerts]
  );

  // Derived loading and error states
  const isLoading = isLoadingMarket || isLoadingCalendar || isLoadingNews;
  const error = errorMarket || errorCalendar || errorNews;

  // Toggle functions for expanding/collapsing alerts
  const toggleMarketAlert = useCallback((id: number) => {
    setExpandedMarketId(prev => prev === id ? null : id);
    // Auto-collapse any other expanded alerts
    setExpandedCalendarId(null);
    setExpandedNewsId(null);
  }, []);

  const toggleCalendarAlert = useCallback((id: number) => {
    setExpandedCalendarId(prev => prev === id ? null : id);
    // Auto-collapse any other expanded alerts
    setExpandedMarketId(null);
    setExpandedNewsId(null);
  }, []);
  
  const toggleNewsAlert = useCallback((id: number) => {
    setExpandedNewsId(prev => prev === id ? null : id);
    // Auto-collapse any other expanded alerts
    setExpandedMarketId(null);
    setExpandedCalendarId(null);
  }, []);

  // Memoize navigation handler
  const handleNavigate = useCallback((id: number, type?: string) => {
    navigate(`/alerts?id=${id}${type ? `&type=${type}` : ''}`);
  }, [navigate]);
  
  // Memoize view more handler
  const handleViewMoreClick = useCallback(() => {
    navigate('/alerts');
  }, [navigate]);

  if (isLoading) {
    return (
      <Card className={cn(
        'overflow-hidden border border-white/10',
        'bg-gradient-to-b from-sidebar/80 to-sidebar backdrop-blur-sm',
        'min-h-[280px] flex flex-col', 
        className
      )}>
        <div className="p-6 flex flex-col gap-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-yellow-400/10 rounded-lg">
                <Sparkles className="w-5 h-5 text-yellow-400" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-white">Alpha Alerts</h2>
                <p className="text-xs text-white/50">Real-time market intelligence</p>
              </div>
            </div>
          </div>
          <Skeleton className="h-6 w-3/4 my-2 bg-white/5" />
          <Skeleton className="h-20 w-full my-2 bg-white/5" />
          <Skeleton className="h-20 w-full my-2 bg-white/5" />
        </div>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className={cn(
        'overflow-hidden border border-white/10',
        'bg-gradient-to-b from-sidebar/80 to-sidebar backdrop-blur-sm',
        'min-h-[280px] flex flex-col', 
        className
      )}>
        <div className="p-6 flex flex-col gap-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-yellow-400/10 rounded-lg">
                <Sparkles className="w-5 h-5 text-yellow-400" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-white">Alpha Alerts</h2>
                <p className="text-xs text-white/50">Real-time market intelligence</p>
              </div>
            </div>
          </div>
          <div className="text-red-400 bg-red-500/10 p-3 rounded-md border border-red-500/20">
            Failed to load alerts. Please try again later.
          </div>
        </div>
      </Card>
    );
  }

  // Determine if we should show compact or expanded view
  const showCompactView = !expandedMarketId && !expandedCalendarId && !expandedNewsId;

  return (
    <Card className={cn(
      'overflow-hidden border border-white/10',
      'bg-gradient-to-b from-sidebar/80 to-sidebar backdrop-blur-sm',
      'min-h-[280px] flex flex-col', 
      className
    )}>
      <div className="p-6 flex flex-col gap-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-yellow-400/10 rounded-lg">
              <Sparkles className="w-5 h-5 text-yellow-400" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-white">Alpha Alerts</h2>
              <p className="text-xs text-white/50">Real-time market intelligence</p>
            </div>
          </div>
        </div>
        
        {/* Expanded Market Alert View */}
        {expandedMarketId && (
          <div className="mb-4">
            {recentMarketAlerts
              .filter(alert => alert.id === expandedMarketId)
              .map(alert => (
                <MarketAlertCard
                  key={alert.id}
                  alert={alert}
                  isExpanded={true}
                  toggleExpand={toggleMarketAlert}
                />
              ))
            }
          </div>
        )}
        
        {/* Expanded Calendar Alert View */}
        {expandedCalendarId && (
          <div className="mb-4">
            {recentCalendarAlerts
              .filter(alert => alert.id === expandedCalendarId)
              .map(alert => (
                <CalendarAlertCard
                  key={alert.id}
                  alert={alert}
                  isExpanded={true}
                  toggleExpand={toggleCalendarAlert}
                />
              ))
            }
          </div>
        )}
        
        {/* Expanded News Alert View */}
        {expandedNewsId && recentNewsAlerts.length > 0 && (
          <div className="mb-4">
            {recentNewsAlerts
              .filter(alert => alert.id === expandedNewsId)
              .map(alert => {
                // Get sentiment emoji
                const getSentimentEmoji = (sentiment: string) => {
                  switch(sentiment) {
                    case 'Bullish': return '🟢';
                    case 'Bearish': return '🔴';
                    default: return '⚪';
                  }
                };

                return (
                  <div key={alert.id} className="bg-black/30 border border-white/10 rounded-lg p-4">
                    <div className="flex justify-between items-start mb-3">
                      <h3 className="text-white font-medium">Market News Summary</h3>
                      <Badge className="bg-purple-900/40 text-purple-400 border-purple-900/50">
                        {getSentimentEmoji(alert.sentiment)} {alert.sentiment}
                      </Badge>
                    </div>
                    <div className="text-white/80 text-sm mb-3">
                      {alert.summary}
                    </div>
                    <div className="mt-3 border-t border-white/10 pt-3">
                      <div className="text-xs text-white/90 font-semibold mb-1">Sentiment Analysis</div>
                      <div className="text-white/80 text-xs mb-3">
                        {alert.sentiment_reasoning}
                      </div>
                      
                      {alert.news_articles && alert.news_articles.length > 0 && (
                        <div>
                          <div className="text-xs text-white/90 font-semibold mb-1">Top Source</div>
                          <div className="text-white/80 text-xs">
                            {alert.news_articles[0].headline}
                          </div>
                          <div className="flex justify-between items-center mt-1">
                            <div className="text-xs text-white/60">{alert.news_articles[0].source}</div>
                            <div className="text-xs text-white/60">{alert.news_articles[0].date}</div>
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="flex justify-between items-center mt-3">
                      <div className="text-xs text-white/60">
                        <Clock className="h-3 w-3 inline mr-1" />
                        {formatDistanceToNow(new Date(alert.timestamp), { addSuffix: true })}
                      </div>
                    </div>
                  </div>
                );
              })}
          </div>
        )}
        
        {/* Compact View */}
        {showCompactView && (
          <div className="space-y-6">
            {/* Market Alerts Section with updated styling */}
            <div>
              <div className="flex items-center gap-2 mb-3">
                <div className="h-px flex-1 bg-gradient-to-r from-yellow-400/20 to-transparent" />
                <h2 className="font-medium text-sm text-yellow-400/90">Market Alerts</h2>
                <div className="h-px flex-1 bg-gradient-to-l from-yellow-400/20 to-transparent" />
              </div>
              {isLoadingMarket ? (
                <Skeleton className="h-24 w-full bg-white/5 rounded-lg" />
              ) : recentMarketAlerts.length === 0 ? (
                <div className="text-white/50 text-sm bg-black/10 p-4 rounded-lg border border-white/5 text-center">
                  No recent market alerts.
                </div>
              ) : (
                <div className="space-y-3">
                  {recentMarketAlerts.map((alert) => (
                    <div 
                      key={`market-${alert.id}`}
                      onClick={() => toggleMarketAlert(alert.id)} 
                      className="bg-black/20 border border-white/10 hover:border-white/20 rounded-lg p-4 cursor-pointer transition-all hover:bg-black/30"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-yellow-400 text-lg">•</span>
                            <h3 className="text-sm font-semibold text-white truncate">
                              {alert.short_summary.split(':')[1]?.trim() || alert.short_summary}
                            </h3>
                          </div>
                          <p className="text-xs text-white/60 line-clamp-2 pl-6">
                            {alert.full_analysis?.extended_summary || alert.short_summary}
                          </p>
                        </div>
                        <Badge className="shrink-0 bg-yellow-900/30 text-yellow-400 border-yellow-900/40 text-[10px] px-2 py-0.5">
                          {alert.risk_level} Risk
                        </Badge>
                      </div>
                      <div className="flex justify-end items-center mt-3 pt-2 border-t border-white/5">
                        <div className="text-white/40 text-[10px] flex items-center">
                          <Clock className="h-3 w-3 mr-1" />
                          {formatDistanceToNow(new Date(alert.timestamp), { addSuffix: true })}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            {/* Calendar Alerts Section with updated styling */}
            <div>
              <div className="flex items-center gap-2 mb-3">
                <div className="h-px flex-1 bg-gradient-to-r from-blue-400/20 to-transparent" />
                <h2 className="font-medium text-sm text-blue-400/90">Calendar Alerts</h2>
                <div className="h-px flex-1 bg-gradient-to-l from-blue-400/20 to-transparent" />
              </div>
              {isLoadingCalendar ? (
                <Skeleton className="h-24 w-full bg-white/5 rounded-lg" />
              ) : recentCalendarAlerts.length === 0 ? (
                <div className="text-white/50 text-sm bg-black/10 p-4 rounded-lg border border-white/5 text-center">
                  No recent calendar alerts.
                </div>
              ) : (
                <div className="space-y-3">
                  {recentCalendarAlerts.map((alert) => (
                    <div 
                      key={`calendar-${alert.id}`}
                      onClick={() => toggleCalendarAlert(alert.id)} 
                      className="bg-black/20 border border-white/10 hover:border-white/20 rounded-lg p-4 cursor-pointer transition-all hover:bg-black/30"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start gap-2">
                            <span className="text-blue-400 text-lg mt-0.5">⚡</span>
                            <p className="text-sm text-white/90 line-clamp-2">
                              {alert.short_summary}
                            </p>
                          </div>
                        </div>
                        <Badge className="shrink-0 bg-blue-900/30 text-blue-400 border-blue-900/40 text-[10px] px-2 py-0.5">
                          {alert.window_volatility_intensity || 'Extreme'} Impact
                        </Badge>
                      </div>
                      <div className="flex justify-end items-center mt-3 pt-2 border-t border-white/5">
                        <div className="text-white/40 text-[10px] flex items-center">
                          <Clock className="h-3 w-3 mr-1" />
                          {formatDistanceToNow(new Date(alert.timestamp), { addSuffix: true })}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            {/* News Alerts Section */}
            <div>
              <div className="flex items-center gap-2 mb-3">
                <div className="h-px flex-1 bg-gradient-to-r from-purple-400/20 to-transparent" />
                <h2 className="font-medium text-sm text-purple-400/90">News Alerts</h2>
                <div className="h-px flex-1 bg-gradient-to-l from-purple-400/20 to-transparent" />
              </div>
              {isLoadingNews ? (
                <Skeleton className="h-24 w-full bg-white/5 rounded-lg" />
              ) : recentNewsAlerts.length === 0 ? (
                <div className="text-white/50 text-sm bg-black/10 p-4 rounded-lg border border-white/5 text-center">
                  No recent news alerts.
                </div>
              ) : (
                <div className="space-y-3">
                  {recentNewsAlerts.map((alert) => {
                    // Get sentiment emoji
                    const getSentimentEmoji = (sentiment: string) => {
                      switch(sentiment) {
                        case 'Bullish': return '🟢';
                        case 'Bearish': return '🔴';
                        default: return '⚪';
                      }
                    };
                    
                    return (
                      <div
                        key={`news-${alert.id}`}
                        className="bg-black/20 border border-white/10 hover:border-white/20 rounded-lg p-4 cursor-pointer transition-all hover:bg-black/30"
                        onClick={() => toggleNewsAlert(alert.id)}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-purple-400 text-lg">{getSentimentEmoji(alert.sentiment)}</span>
                              <h3 className="text-sm font-semibold text-white">Market News Summary</h3>
                            </div>
                            <p className="text-xs text-white/60 line-clamp-2 pl-6">
                              {alert.summary}
                            </p>
                          </div>
                          <Badge className="shrink-0 bg-purple-900/30 text-purple-400 border-purple-900/40 text-[10px] px-2 py-0.5">
                            {alert.sentiment}
                          </Badge>
                        </div>
                        <div className="flex justify-between items-center mt-3 pt-2 border-t border-white/5">
                          <div className="text-white/40 text-[10px]">
                            {alert.news_articles?.[0]?.source || 'News Source'}
                          </div>
                          <div className="text-white/40 text-[10px] flex items-center">
                            <Clock className="h-3 w-3 mr-1" />
                            {formatDistanceToNow(new Date(alert.timestamp), { addSuffix: true })}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      
      <CardFooter className="border-t border-white/10 bg-black/20 p-3">
        <Button 
          variant="ghost" 
          className="w-full justify-between text-yellow-400 hover:text-yellow-300 hover:bg-yellow-900/20"
          onClick={handleViewMoreClick}
        >
          <span>View all alerts</span>
          <ArrowRight className="h-4 w-4" />
        </Button>
      </CardFooter>
    </Card>
  );
});

StrykrAlertsCard.displayName = 'StrykrAlertsCard';

export default StrykrAlertsCard;
